import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'

import { formatMoney } from "helpers/helpers";
import { GET, httpOk } from 'helpers/api'

// Telegram отвечает по-английски, а читает это владелец заведения. Подбираем объяснение
// по тексту ответа; сам ответ всё равно показываем рядом — по нему ищется поддержка.
const ERROR_RULES = [
	{ key: 'tg_error_blocked', markers: ['bot was blocked by the user', 'user_is_blocked'] },
	{ key: 'tg_error_deactivated', markers: ['user is deactivated'] },
	{ key: 'tg_error_chat_not_found', markers: ['chat not found', 'peer_id_invalid', 'user not found', 'bot was kicked', 'chat_id is empty', "can't initiate conversation"] },
	{ key: 'tg_error_url_content', markers: ['failed to get http url content', 'wrong type of the web page content'] },
	{ key: 'tg_error_file_id', markers: ['wrong file identifier', 'wrong remote file', 'media_empty', 'photo_save_file_invalid', 'image_process_failed'] },
	{ key: 'tg_error_caption_long', markers: ['caption is too long'] },
	{ key: 'tg_error_parse', markers: ["can't parse entities"] },
	{ key: 'tg_error_dimensions', markers: ['photo_invalid_dimensions'] },
	{ key: 'tg_error_too_big', markers: ['too big'] },
	// в fail_reason кода нет — только текст, который сервер записал при остановке
	{ key: 'tg_error_token', markers: ['unauthorized', 'telegram: not found'] },
]

export function tgErrorKey(code, message) {
	const text = (message ?? '').toLowerCase()
	const rule = ERROR_RULES.find(rule => rule.markers.some(marker => text.includes(marker)))
	if (rule) return rule.key

	if (code === 401 || code === 404) return 'tg_error_token'
	if (code === 429) return 'tg_error_rate'
	if (code === 0) return 'tg_error_network'

	return 'tg_error_unknown'
}

function ErrorsModal({ notification, onHide }) {
	const { t } = useTranslation()

	const [data, setData] = useState(null)
	const [openedGroup, setOpenedGroup] = useState(null)

	useEffect(() => {
		if (!notification) return

		setData(null)
		setOpenedGroup(null)

		async function load() {
			const response = await GET(`/telegram-notifications/${notification.id}/errors`, {}, { loader: true })
			if (httpOk(response)) setData(response.data)
		}
		load()
	}, [notification])

	return (
		<Modal show={!!notification} animation={false} centered scrollable size="lg" onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>{t('tg_errors_title')}</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ 'height': '500px' }}>
				{data?.fail_reason &&
					<div className="alert alert-danger">
						<div className="fw-bold">{t('not_sent')}</div>
						<div>{t(tgErrorKey(null, data.fail_reason))}</div>
						{data.groups?.length > 0 &&
							<div className="mt-1">{t('tg_broadcast_stopped')}</div>
						}
						<div className="mt-2 fz-14 text-muted">
							{t('tg_errors_telegram_answer')}: <code>{data.fail_reason}</code>
						</div>
					</div>
				}

				{data?.groups?.map((group, index) => (
					<div className="border rounded p-3 mb-3" key={index}>
						<div className="d-flex justify-content-between align-items-start gap-3">
							<div>
								<div className="fw-bold">{t(tgErrorKey(group.error_code, group.message))}</div>
								<div className="mt-1 fz-14 text-muted">
									{t('tg_errors_telegram_answer')}: <code>{group.error_code ? `${group.error_code} · ` : ''}{group.message}</code>
								</div>
							</div>
							<span className="badge bg-danger fz-14 text-nowrap">{formatMoney(group.count)}</span>
						</div>

						{group.clients?.length > 0 &&
							<div className="mt-2">
								<span className="text-primary cursor"
									onClick={() => setOpenedGroup(openedGroup === index ? null : index)}>
									<i className={openedGroup === index ? 'uil uil-angle-up' : 'uil uil-angle-down'}></i>
									{' '}{t('tg_errors_recipients')} ({formatMoney(group.clients.length)})
								</span>

								{openedGroup === index &&
									<table className="table table-bordered table-sm mt-2 mb-0">
										<thead>
											<tr>
												<th>{t('client')}</th>
												<th>{t('phone')}</th>
												<th>{t('telegram')}</th>
											</tr>
										</thead>
										<tbody>
											{group.clients.map(client => (
												<tr key={client.id}>
													{/* у подписчиков из бота имя часто и есть номер — не дублируем его */}
													<td>{client.name && client.name !== client.phone ? client.name : ''}</td>
													<td className="text-nowrap">{client.phone}</td>
													<td>{client.telegram_username ? `@${client.telegram_username}` : ''}</td>
												</tr>
											))}
										</tbody>
									</table>
								}
							</div>
						}
					</div>
				))}

				{data && data.stored_count < data.errors_count &&
					<div className="text-muted fz-14">
						{t('tg_errors_shown_part', { count: formatMoney(data.stored_count), total: formatMoney(data.errors_count) })}
					</div>
				}

				{data && !data.fail_reason && !data.groups?.length &&
					<div className="text-center text-muted">{t('no_data')}</div>
				}
			</Modal.Body>
		</Modal>
	)
}

export default ErrorsModal
